import { ColDef } from 'ag-grid-community';
import { IAgent } from '../../types/agent';
import { IBrand } from '../../types/brand';
import { BtnDefault } from '../common/Buttons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../common/Dropdown';
import { Language } from '../../api/queries';
import { openAssetByKey } from '../../utils/openAssetByKey';

interface AgentColumnParams {
  onWorkLogs: (data: IAgent) => void;
  onView: (data: IAgent) => void;
  onEdit: (data: IAgent) => void;
  onChangePassword: (data: IAgent) => void;
  brandsData: IBrand[];
}

const AgentActionsCell: React.FC<{
  data: IAgent;
  onView: (data: IAgent) => void;
  onEdit: (data: IAgent) => void;
  onChangePassword: (data: IAgent) => void;
  onWorkLogs: (data: IAgent) => void;
}> = ({ data, onView, onEdit, onChangePassword, onWorkLogs }) => {
  return (
    <div className="space-x-2 space-y-2 flex items-center">
      <BtnDefault onClick={() => onWorkLogs(data)}>Work Logs</BtnDefault>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <BtnDefault className="relative bottom-1">More</BtnDefault>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem
            onClick={() => onView(data)}
            className="text-start w-full cursor-pointer"
          >
            View Agent
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onEdit(data)}
            className="text-start w-full cursor-pointer"
          >
            Edit Agent
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onChangePassword(data)}
            className="text-start w-full cursor-pointer"
          >
            Change Password
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const useAgentColumns = ({
  onView,
  onEdit,
  onChangePassword,
  onWorkLogs,
  brandsData,
}: AgentColumnParams): ColDef<IAgent>[] => {
  return [
    ...agentColumnsDefs(brandsData), // Visible columns
    ...hiddenAgentExportColumns, // Include hidden columns for export
    {
      headerName: 'Actions',
      field: '_id',
      width: 200,
      sortable: false,
      filter: false,
      minWidth: 200,
      suppressSizeToFit: true,
      pinned: 'right',
      lockPinned: true,
      cellRenderer: (params: any) => (
        <AgentActionsCell
          data={params.data}
          onView={onView}
          onEdit={onEdit}
          onChangePassword={onChangePassword}
          onWorkLogs={onWorkLogs}
        />
      ),
    },
  ];
};

const getBrandName = (data: any, agent: IAgent) => {
  if (!data || !data.brands || !agent) {
    return '';
  }

  const id = agent.kimId;
  const names = data.brands.filter((brand: any) => brand.agents.includes(id));
  return names?.map((brand: any) => brand.name).join(', ') || '';
};

// Hidden columns for export - all additional fields from the database
const hiddenAgentExportColumns: ColDef<IAgent>[] = [
  {
    field: 'dob',
    headerName: 'Date of Birth',
    hide: true,
    valueFormatter: (params: any) =>
      params.value ? new Date(params.value).toLocaleDateString() : '',
  },
  {
    field: 'phoneNumber.countryCode',
    headerName: 'Phone Country Code',
    hide: true,
  },
  {
    field: 'phoneNumber.number',
    headerName: 'Phone Number',
    hide: true,
  },
  {
    field: 'phoneNumber.countryCodeEnum',
    headerName: 'Phone Country',
    hide: true,
  },
  {
    field: 'whatsappNumber.countryCode',
    headerName: 'WhatsApp Country Code',
    hide: true,
  },
  {
    field: 'whatsappNumber.countryCodeEnum',
    headerName: 'WhatsApp Country',
    hide: true,
  },
  {
    field: 'governmentId',
    headerName: 'Government ID',
    hide: true,
  },
  {
    field: 'emergencyContact.name',
    headerName: 'Emergency Contact Name',
    hide: true,
  },
  {
    field: 'emergencyContact.phoneNumber',
    headerName: 'Emergency Contact Phone',
    hide: true,
  },
  {
    field: 'currentAddress',
    headerName: 'Current Address',
    hide: true,
  },
  {
    field: 'pastEmployers',
    headerName: 'Past Employers',
    hide: true,
    valueGetter: (params: any) => {
      const employers = params.data?.pastEmployers;
      if (!employers || !Array.isArray(employers) || employers.length === 0)
        return '';
      return employers
        .map((emp: any) => emp.name || emp.company || JSON.stringify(emp))
        .join('; ');
    },
  },
  {
    field: 'totalExperience',
    headerName: 'Total Experience',
    hide: true,
  },
  {
    field: 'hobbies',
    headerName: 'Hobbies',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.hobbies)
        ? params.data.hobbies.join(', ')
        : '';
    },
  },
  {
    field: 'upworkProfile',
    headerName: 'Upwork Profile',
    hide: true,
  },
  {
    field: 'bankDetails.name',
    headerName: 'Bank Account Name',
    hide: true,
  },
  {
    field: 'bankDetails.bankName',
    headerName: 'Bank Name',
    hide: true,
  },
  {
    field: 'bankDetails.accountNumber',
    headerName: 'Account Number',
    hide: true,
  },
  {
    field: 'bankDetails.accountType',
    headerName: 'Account Type',
    hide: true,
  },
  {
    field: 'bankDetails.bankCode',
    headerName: 'Bank Code',
    hide: true,
  },
  {
    field: 'bankDetails.branchName',
    headerName: 'Branch Name',
    hide: true,
  },
  {
    field: 'paymentIds.paypalId',
    headerName: 'PayPal ID',
    hide: true,
  },
  {
    field: 'paymentIds.payoneerId',
    headerName: 'Payoneer ID',
    hide: true,
  },
  {
    field: 'paymentIds.wiseId',
    headerName: 'Wise ID',
    hide: true,
  },
  {
    field: 'preferredPaymentMethod',
    headerName: 'Preferred Payment Method',
    hide: true,
  },
  {
    field: 'passportNumber',
    headerName: 'Passport Number',
    hide: true,
  },
  {
    field: 'industry',
    headerName: 'Industry',
    hide: true,
  },
  {
    field: 'relevantIndustries',
    headerName: 'Relevant Industries',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.relevantIndustries)
        ? params.data.relevantIndustries.join(', ')
        : '';
    },
  },
];

const agentColumnsDefs = (brandsData?: any): ColDef<IAgent>[] => [
  {
    headerName: 'KIM ID',
    field: 'kimId',
    minWidth: 120,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Name',
    field: 'name',
    minWidth: 150,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Personal Email',
    field: 'personalEmail',
    minWidth: 200,
    cellRenderer: (params: any) => {
      return params.value ? (
        <a href={`mailto:${params.value}`}>{params.value}</a>
      ) : null;
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Whatsapp Number',
    field: 'whatsappNumber',
    minWidth: 150,
    valueGetter: (params) => {
      return params.data?.whatsappNumber?.number;
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Profile Image',
    field: 'profileUri',
    minWidth: 120,
    cellRenderer: (params: any) =>
      params.value ? (
        <button className="underline" onClick={() => openAssetByKey(params.value, 'profile')}>
          View Picture
        </button>
      ) : null,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Resume',
    field: 'resumeUri',
    minWidth: 120,
    cellRenderer: (params: any) =>
      params.value ? (
        <button className="underline" onClick={() => openAssetByKey(params.value, 'resume')}>
          View Resume
        </button>
      ) : null,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Application Video',
    field: 'applicationVideoUri',
    minWidth: 120,
    cellRenderer: (params: any) =>
      params.value ? (
        <button className="underline" onClick={() => openAssetByKey(params.value, 'video')}>
          View Video
        </button>
      ) : null,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Bank Statement',
    field: 'bankDetails.bankStatement',
    minWidth: 120,
    cellRenderer: (params: any) => {
      const bankStatement = params.data?.bankDetails?.bankStatement;
      return bankStatement ? (
        <button className="underline" onClick={() => openAssetByKey(bankStatement, 'bank statement')}>
          View Statement
        </button>
      ) : '';
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 120,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Created At',
    field: 'createdAt',
    cellDataType: 'date',
    valueGetter: (params: any) => params?.data?.createdAt || '',
    valueFormatter: (params: any) =>
      params.value ? new Date(params.value).toString() : '',
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);

        return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
      },
    },
    sortable: true,
    filter: 'agDateColumnFilter',
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Skills',
    field: 'skills',
    minWidth: 150,
    cellRenderer: (params: any) => {
      return params.value?.join(', ') || '';
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Languages',
    field: 'languages',
    minWidth: 200,
    cellRenderer: (params: any) => {
      return (
        params.value
          ?.map(
            (lang: Language) => `${lang.language} (${lang.fluency.join(', ')})`
          )
          .join('; ') || ''
      );
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Tools',
    field: 'tools',
    minWidth: 150,
    cellRenderer: (params: any) => {
      return params.value?.join(', ') || '';
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Channels',
    field: 'channels',
    minWidth: 150,
    cellRenderer: (params: any) => {
      return params.value?.join(', ') || '';
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Brands',
    field: 'brandIds',
    minWidth: 150,
    cellRenderer: (params: any) => {
      return getBrandName(brandsData, params.data);
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Updated At',
    field: 'updatedAt',
    cellDataType: 'date',
    minWidth: 180,
    valueGetter: (params: any) => params.data?.updatedAt || '',
    valueFormatter: (params: any) =>
      params.value ? new Date(params.value).toString() : '',
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);

        return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
      },
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Comments',
    field: 'comments',
    minWidth: 150,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
];
